import { useEffect } from 'react';
import { range } from 'lodash-es';
import { format, isAfter, startOfToday } from 'date-fns';

import {
  AdornedInput,
  Adornment,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Paragraph,
  Radio,
  Select,
  Tooltip,
  TooltipPopper,
} from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models';
import { BASE_RENT, LEASE_TERMS } from '../../../constants/index';
import { CURRENCY_INPUT_PLACEHOLDER, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import { getOrdinal } from '../../../utils/humanReadable';
import InlineTooltip from '../../../components/Widgets/InlineTooltip';

const LAST_DAY_OF_MONTH = 32;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

// BaseRent with additional selection for radio buttons.
export default function BaseRent_v2({ section, setSectionValues }: Props) {
  useEffect(() => {
    section.setCustomFieldValidator(BASE_RENT.FIELD_NAME.AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > BASE_RENT.BOUNDS.MAX_AMOUNT) {
      section
        .getField(BASE_RENT.FIELD_NAME.AMOUNT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(BASE_RENT.BOUNDS.MAX_AMOUNT));
      return false;
    }
    return true;
  }

  function onAmountChange(event: any): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [BASE_RENT.FIELD_NAME.AMOUNT]: value,
    });
  }

  function onDueDateChange(event: any): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [BASE_RENT.FIELD_NAME.DUE_DATE]: Number(value),
    });
  }

  function onPayDueClick(event: any): void {
    const { id } = event.currentTarget;

    setSectionValues({
      [BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_SIGN]: id === 'firstPayOnSign',
      [BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_START]: id === 'firstPayOnLeaseStart',
      [BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_MONTHLY_START]: id === 'firstPayOnMonthlyLeaseStart',
    });
  }

  function startDayOfTheLeaseLabel(): string {
    const startDate = section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
    const formattedDate = startDate && format(startDate, 'MM/DD/YYYY');

    return `The lease start/move-in date ${formattedDate ? `(${formattedDate})` : ''}`;
  }

  const dueDay = section.getFieldValue(BASE_RENT.FIELD_NAME.DUE_DATE);

  return (
    <>
      <Heading level={6} marginBottom="xs">
        Tell us about the rent
      </Heading>
      <FormField
        marginBottom="sm"
        disabled={section.isFieldDisabled(BASE_RENT.FIELD_NAME.AMOUNT)}
        label={
          <Label id="base-rent-amount">
            How much is{' '}
            <InlineTooltip
              tooltipBodyText="Base rent is the rent before other fees such as parking or utilities are added."
              closeWith={['blur']}
              openWith={['focus', 'click', 'mouseEnter']}
              shouldCloseOnOutsideClick
            >
              base rent
            </InlineTooltip>
            ?
          </Label>
        }
        control={
          <AdornedInput
            inputId="AdornedInput-base-rent"
            leftAdornment={<Adornment>$</Adornment>}
            rightAdornment={<Adornment>per month</Adornment>}
            input={
              <CurrencyInput
                aria-labelledby="base-rent-amount"
                placeholder={CURRENCY_INPUT_PLACEHOLDER}
                value={section.getFieldValue(BASE_RENT.FIELD_NAME.AMOUNT)}
                onChange={onAmountChange}
                onBlur={onAmountChange}
              />
            }
            error={section.getField(BASE_RENT.FIELD_NAME.AMOUNT).hasError}
          />
        }
        description={
          section.getField(BASE_RENT.FIELD_NAME.AMOUNT).hasError && (
            <FormHelp error>{section.getField(BASE_RENT.FIELD_NAME.AMOUNT).getErrorMessage()}</FormHelp>
          )
        }
      />
      <FormField
        marginBottom="sm"
        disabled={section.isFieldDisabled(BASE_RENT.FIELD_NAME.DUE_DATE)}
        label={<Label id="monthly-due-date">What is the monthly due date?</Label>}
        control={
          <Select aria-labelledby="monthly-due-date" onChange={onDueDateChange} value={dueDay || ''}>
            {range(1, 29).map((day) => (
              <option key={day} value={day}>
                {dueDay === day ? `${getOrdinal(day)} day of the month` : day}
              </option>
            ))}
            <option value={LAST_DAY_OF_MONTH}>Last day of the month</option>
          </Select>
        }
      />
      <FieldSet
        legend={<Legend>When is the first monthly rent payment due?</Legend>}
        error={
          section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE) &&
          isAfter(startOfToday(), section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE))
        }
        description={
          section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE) &&
          isAfter(startOfToday(), section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)) ? (
            <FormHelp>
              Start date cannot be in the past, first change it on the <strong>Lease terms</strong> screen
            </FormHelp>
          ) : (
            <FormHelp>Monthly rent includes the base rent and other fixed monthly fees, such as parking</FormHelp>
          )
        }
      >
        <LabeledControl
          controlId="firstPayOnSign"
          label={<Label>Upon signing the lease</Label>}
          disabled={section.isFieldDisabled(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_SIGN)}
          control={
            <Radio
              name="rent-payment-due"
              checked={section.getFieldValue(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_SIGN)}
              onChange={onPayDueClick}
            />
          }
        />
        <LabeledControl
          controlId="firstPayOnLeaseStart"
          label={<Label>{startDayOfTheLeaseLabel()}</Label>}
          control={
            <Radio
              name="rent-payment-due"
              checked={section.getFieldValue(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_START)}
              onChange={onPayDueClick}
            />
          }
        />
        <LabeledControl
          controlId="firstPayOnMonthlyLeaseStart"
          label={
            <Label>
              The first monthly rent due date after the start date{' '}
              <TooltipPopper
                triggered={
                  <Tooltip
                    body={
                      <Paragraph>
                        This may apply when the move-in date is in the middle of the month and does not include any
                        prorated rent to account for the first partial month of occupancy. By selecting this option, the
                        prorated rent for the first partial month of occupancy will be due at the lease start/move-in
                        date.
                      </Paragraph>
                    }
                  />
                }
              />
            </Label>
          }
          control={
            <Radio
              name="rent-payment-due"
              checked={section.getFieldValue(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_MONTHLY_START)}
              onChange={onPayDueClick}
            />
          }
        />
      </FieldSet>
    </>
  );
}
